import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LayoutGrid, List, Pencil, Play, Plus, Search, Trash2, X } from "lucide-react";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { SelmaAdminMobileNav, SelmaAdminSidebar } from "@/components/admin/SelmaAdminSidebar";
import { SelmaAiVoiceGenerator } from "@/components/admin/SelmaAiVoiceGenerator";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

// Numeroloji admin panelindeki "Meditasyon Yönetimi" sayfasının yeniden adlandırılmış
// hali — form alanları aynı, tablo/endpoint izole: duygusal_sebep_meditasyonlari /
// /api/admin/selma/duygusal-sebep. Bkz. CLAUDE.md → "Selma Admin Paneli".

type DuygusalSebep = {
  id: string;
  baslik: string;
  aciklama: string | null;
  ses_url: string | null;
  sure_dakika: number | null;
  kategori_duygu: string[];
  kategori_beden_bolgesi: string[];
  gorsel_url: string | null;
  music_url: string | null;
  music_volume: number;
  price: number;
  created_at: string;
};

const EMPTY_FORM = {
  baslik: "",
  aciklama: "",
  ses_url: "",
  sure_dakika: "",
  gorsel_url: "",
  music_url: "",
  music_volume: "0.5",
  price: "0",
  kategoriDuyguInput: "",
  kategoriBedenInput: "",
};
type FormState = typeof EMPTY_FORM & { kategori_duygu: string[]; kategori_beden_bolgesi: string[] };
const emptyFormFull = (): FormState => ({ ...EMPTY_FORM, kategori_duygu: [], kategori_beden_bolgesi: [] });

async function fetchItems(): Promise<DuygusalSebep[]> {
  const res = await adminFetch("/api/admin/selma/duygusal-sebep");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Liste alınamadı.");
  return Array.isArray(json?.data) ? json.data : [];
}

async function createItem(payload: Record<string, unknown>): Promise<void> {
  const res = await adminFetch("/api/admin/selma/duygusal-sebep", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Kaydedilemedi.");
}

async function updateItem(payload: Record<string, unknown> & { id: string }): Promise<void> {
  const res = await adminFetch(`/api/admin/selma/duygusal-sebep/${encodeURIComponent(payload.id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Güncellenemedi.");
}

async function deleteItem(id: string): Promise<void> {
  const res = await adminFetch(`/api/admin/selma/duygusal-sebep/${encodeURIComponent(id)}`, { method: "DELETE" });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Silinemedi.");
}

type MusicOption = { id: string; isim: string; dosya_url: string };

async function fetchMusicOptions(): Promise<MusicOption[]> {
  const res = await adminFetch("/api/admin/selma/meditasyon-muzikleri");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Müzik listesi alınamadı.");
  return Array.isArray(json?.data) ? json.data : [];
}

async function uploadFile(file: File, folder: string): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("bucket", "duygusifa-media");
  formData.append("folder", folder);
  const res = await adminFetch("/api/admin/upload", { method: "POST", body: formData });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Yükleme başarısız.");
  return json.url as string;
}

function TagInput({
  label,
  values,
  inputValue,
  onInputChange,
  onAdd,
  onRemove,
}: {
  label: string;
  values: string[];
  inputValue: string;
  onInputChange: (v: string) => void;
  onAdd: (v: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <div className="flex flex-wrap gap-1.5 rounded-xl border border-slate-200 bg-white p-2">
        {values.map((tag, i) => (
          <span key={i} className="flex items-center gap-1 rounded-full bg-emerald-600/10 px-2 py-0.5 text-xs font-semibold text-emerald-700">
            {tag}
            <button type="button" onClick={() => onRemove(i)} className="leading-none opacity-70 hover:opacity-100">
              ×
            </button>
          </span>
        ))}
        <input
          className="min-w-[120px] flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
          placeholder="Yaz, Enter'a bas..."
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === ",") && inputValue.trim()) {
              e.preventDefault();
              onAdd(inputValue.trim().replace(/,$/, ""));
            }
          }}
        />
      </div>
      <div className="mt-1 text-[11px] text-slate-400">Enter veya virgül ile ekle.</div>
    </div>
  );
}

export default function AdminSelmaDuygusalSebep() {
  const queryClient = useQueryClient();
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(emptyFormFull());
  const [editingItem, setEditingItem] = useState<DuygusalSebep | null>(null);
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const [audio] = useState(() => (typeof Audio !== "undefined" ? new Audio() : null));
  const [formImgLoading, setFormImgLoading] = useState(false);
  const [formSesLoading, setFormSesLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"newest" | "title-asc" | "duration-asc" | "duration-desc">("newest");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const listQuery = useQuery({
    queryKey: ["selma-duygusal-sebep"],
    queryFn: fetchItems,
    enabled: authenticated,
    staleTime: 0,
  });

  // music_url artık serbest upload değil — duygusifa_meditasyon_muzikleri tablosundan seçim
  const musicQuery = useQuery({
    queryKey: ["selma-meditasyon-muzikleri-options"],
    queryFn: fetchMusicOptions,
    enabled: authenticated,
    staleTime: 0,
  });
  const musicOptions = musicQuery.data ?? [];

  const createMutation = useMutation({
    mutationFn: createItem,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["selma-duygusal-sebep"], exact: true });
      setShowForm(false);
      setForm(emptyFormFull());
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateItem,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["selma-duygusal-sebep"], exact: true });
      setEditingItem(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteItem,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["selma-duygusal-sebep"], exact: true });
    },
  });

  function handlePlay(url: string) {
    if (!audio) return;
    if (playingUrl === url) {
      audio.pause();
      audio.currentTime = 0;
      setPlayingUrl(null);
    } else {
      audio.src = url;
      audio.play();
      setPlayingUrl(url);
      audio.onended = () => setPlayingUrl(null);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (createMutation.isPending || !form.baslik.trim()) return;
    createMutation.mutate({
      baslik: form.baslik.trim(),
      aciklama: form.aciklama.trim() || null,
      ses_url: form.ses_url.trim() || null,
      sure_dakika: form.sure_dakika ? Number(form.sure_dakika) : null,
      kategori_duygu: form.kategori_duygu,
      kategori_beden_bolgesi: form.kategori_beden_bolgesi,
      gorsel_url: form.gorsel_url.trim() || null,
      music_url: form.music_url.trim() || null,
      music_volume: form.music_volume ? Number(form.music_volume) : 0.5,
      price: form.price ? Number(form.price) : 0,
    });
  }

  const items = listQuery.data ?? [];

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = q
      ? items.filter((it) => {
          const hay = `${it.baslik} ${it.aciklama ?? ""} ${(it.kategori_duygu ?? []).join(" ")} ${(it.kategori_beden_bolgesi ?? []).join(" ")}`.toLowerCase();
          return hay.includes(q);
        })
      : items;
    const sorted = [...rows];
    if (sort === "title-asc") sorted.sort((a, b) => a.baslik.localeCompare(b.baslik, "tr"));
    else if (sort === "duration-asc") sorted.sort((a, b) => (a.sure_dakika ?? 0) - (b.sure_dakika ?? 0));
    else if (sort === "duration-desc") sorted.sort((a, b) => (b.sure_dakika ?? 0) - (a.sure_dakika ?? 0));
    return sorted;
  }, [items, search, sort]);

  if (sessionQuery.isLoading) {
    return <div className="p-8 text-sm text-slate-500">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full bg-slate-100/60">
      <SelmaAdminSidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <div className="text-base font-semibold text-slate-900 sm:text-lg">Selma — Duygusal Sebep</div>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => logoutMutation.mutate()} disabled={logoutMutation.isPending}>
              Çıkış
            </Button>
          </div>
          <div className="mt-3">
            <SelmaAdminMobileNav />
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          {listQuery.isLoading ? (
            <div className="text-sm text-slate-500">Yükleniyor...</div>
          ) : listQuery.isError ? (
            <div className="text-sm text-destructive">{(listQuery.error as Error).message}</div>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-2xl font-extrabold tracking-tight text-slate-900">Duygusal Sebep Meditasyonları</div>
                  <div className="mt-1 text-sm text-slate-500">Fiziksel/duygusal köke bağlı meditasyon içerikleri.</div>
                </div>
                <Button size="sm" className="rounded-full" onClick={() => setShowForm(true)}>
                  <Plus className="mr-1 h-4 w-4" />
                  Yeni Ekle
                </Button>
              </div>

              {showForm && (
                <form onSubmit={handleSubmit} className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="font-semibold text-slate-900">Yeni Duygusal Sebep Meditasyonu</div>
                    <button type="button" onClick={() => { setShowForm(false); setForm(emptyFormFull()); }}>
                      <X className="h-4 w-4 text-slate-500" />
                    </button>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Başlık *</label>
                      <input
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                        value={form.baslik}
                        onChange={(e) => setForm((f) => ({ ...f, baslik: e.target.value }))}
                        placeholder="örn: Migren ve Zihinsel Baskı"
                        required
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Açıklama</label>
                      <textarea
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                        rows={2}
                        value={form.aciklama}
                        onChange={(e) => setForm((f) => ({ ...f, aciklama: e.target.value }))}
                        placeholder="Duygusal kök neden özeti..."
                      />
                    </div>

                    <TagInput
                      label="Kategori — Duygu"
                      values={form.kategori_duygu}
                      inputValue={form.kategoriDuyguInput}
                      onInputChange={(v) => setForm((f) => ({ ...f, kategoriDuyguInput: v }))}
                      onAdd={(v) =>
                        setForm((f) =>
                          f.kategori_duygu.includes(v)
                            ? { ...f, kategoriDuyguInput: "" }
                            : { ...f, kategori_duygu: [...f.kategori_duygu, v], kategoriDuyguInput: "" },
                        )
                      }
                      onRemove={(i) => setForm((f) => ({ ...f, kategori_duygu: f.kategori_duygu.filter((_, ti) => ti !== i) }))}
                    />

                    <TagInput
                      label="Kategori — Beden Bölgesi"
                      values={form.kategori_beden_bolgesi}
                      inputValue={form.kategoriBedenInput}
                      onInputChange={(v) => setForm((f) => ({ ...f, kategoriBedenInput: v }))}
                      onAdd={(v) =>
                        setForm((f) =>
                          f.kategori_beden_bolgesi.includes(v)
                            ? { ...f, kategoriBedenInput: "" }
                            : { ...f, kategori_beden_bolgesi: [...f.kategori_beden_bolgesi, v], kategoriBedenInput: "" },
                        )
                      }
                      onRemove={(i) => setForm((f) => ({ ...f, kategori_beden_bolgesi: f.kategori_beden_bolgesi.filter((_, ti) => ti !== i) }))}
                    />

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Süre (dakika)</label>
                      <input
                        type="number"
                        min="0"
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                        value={form.sure_dakika}
                        onChange={(e) => setForm((f) => ({ ...f, sure_dakika: e.target.value }))}
                        placeholder="12"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Fiyat (₺) — 0 ise ücretsiz</label>
                      <input
                        type="number"
                        min="0"
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                        value={form.price}
                        onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                        placeholder="0"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Ses Dosyası (ses_url)</label>
                      <div className="flex items-center gap-2">
                        <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
                          {formSesLoading ? "Yükleniyor…" : "Dosya Seç"}
                          <input
                            type="file"
                            accept="audio/*"
                            className="hidden"
                            disabled={formSesLoading}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setFormSesLoading(true);
                              try {
                                const url = await uploadFile(file, "duygusal-sebep/ses");
                                setForm((f) => ({ ...f, ses_url: url }));
                              } catch (err: any) {
                                alert(err.message ?? "Yükleme başarısız.");
                              } finally {
                                setFormSesLoading(false);
                                e.target.value = "";
                              }
                            }}
                          />
                        </label>
                      </div>
                      <input
                        className="mt-2 h-9 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs text-slate-700 outline-none"
                        value={form.ses_url}
                        onChange={(e) => setForm((f) => ({ ...f, ses_url: e.target.value }))}
                        placeholder="ya da URL yapıştır..."
                      />
                      <div className="mt-2">
                        <SelmaAiVoiceGenerator onGenerated={(url) => setForm((f) => ({ ...f, ses_url: url }))} />
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Görsel</label>
                      <div className="flex items-center gap-3">
                        {form.gorsel_url && <img src={form.gorsel_url} alt="" className="h-12 w-12 flex-shrink-0 rounded-xl object-cover" />}
                        <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
                          {formImgLoading ? "Yükleniyor…" : "Görsel Seç"}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={formImgLoading}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setFormImgLoading(true);
                              try {
                                const url = await uploadFile(file, "duygusal-sebep/gorsel");
                                setForm((f) => ({ ...f, gorsel_url: url }));
                              } catch (err: any) {
                                alert(err.message ?? "Yükleme başarısız.");
                              } finally {
                                setFormImgLoading(false);
                                e.target.value = "";
                              }
                            }}
                          />
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Arka Plan Müziği (music_url)</label>
                      <select
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                        value={form.music_url}
                        onChange={(e) => setForm((f) => ({ ...f, music_url: e.target.value }))}
                      >
                        <option value="">— Müzik yok —</option>
                        {musicOptions.map((m) => (
                          <option key={m.id} value={m.dosya_url}>{m.isim}</option>
                        ))}
                      </select>
                      <div className="mt-1 text-[11px] text-slate-400">
                        Liste "Meditasyon Müziği" sayfasından yönetilir.
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Müzik Ses Seviyesi — {Number(form.music_volume).toFixed(2)}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        className="h-10 w-full accent-emerald-600"
                        value={form.music_volume}
                        onChange={(e) => setForm((f) => ({ ...f, music_volume: e.target.value }))}
                      />
                    </div>
                  </div>

                  {createMutation.isError && (
                    <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                      {(createMutation.error as Error).message}
                    </div>
                  )}

                  <div className="mt-4 flex gap-2">
                    <Button type="submit" size="sm" className="rounded-full" disabled={createMutation.isPending}>
                      {createMutation.isPending ? "Kaydediliyor…" : "Kaydet"}
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => { setShowForm(false); setForm(emptyFormFull()); }}>
                      İptal
                    </Button>
                  </div>
                </form>
              )}

              <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    className="h-11 w-full rounded-2xl bg-slate-100 pl-10 pr-4 text-sm text-slate-900 outline-none ring-emerald-600/20 focus:ring-4"
                    placeholder="Başlık, açıklama, kategori..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-medium text-slate-500">Sırala:</div>
                    <select
                      className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none"
                      value={sort}
                      onChange={(e) => setSort(e.target.value as any)}
                    >
                      <option value="newest">En Yeni</option>
                      <option value="title-asc">Başlık (A-Z)</option>
                      <option value="duration-asc">Süre (Artan)</option>
                      <option value="duration-desc">Süre (Azalan)</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1 rounded-2xl border border-slate-200 bg-white p-1">
                    <button
                      type="button"
                      className={cn("grid h-8 w-8 place-items-center rounded-xl transition-colors", viewMode === "grid" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900")}
                      onClick={() => setViewMode("grid")}
                      title="Grid görünümü"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className={cn("grid h-8 w-8 place-items-center rounded-xl transition-colors", viewMode === "list" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900")}
                      onClick={() => setViewMode("list")}
                      title="Liste görünümü"
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {deleteMutation.isError && (
                <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {(deleteMutation.error as Error).message}
                </div>
              )}

              {editingItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      updateMutation.mutate({
                        id: editingItem.id,
                        baslik: editingItem.baslik,
                        aciklama: editingItem.aciklama,
                        ses_url: editingItem.ses_url,
                        sure_dakika: editingItem.sure_dakika,
                        kategori_duygu: editingItem.kategori_duygu ?? [],
                        kategori_beden_bolgesi: editingItem.kategori_beden_bolgesi ?? [],
                        gorsel_url: editingItem.gorsel_url,
                        music_url: editingItem.music_url,
                        music_volume: editingItem.music_volume,
                        price: editingItem.price,
                      });
                    }}
                    className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-slate-200 bg-white shadow-xl"
                  >
                    <div className="flex flex-shrink-0 items-center justify-between rounded-t-2xl border-b border-slate-200 bg-white p-6 pb-4">
                      <div className="font-semibold text-slate-900">Meditasyonu Düzenle</div>
                      <button type="button" onClick={() => setEditingItem(null)}>
                        <X className="h-4 w-4 text-slate-500" />
                      </button>
                    </div>
                    <div className="overflow-y-auto p-6">
                      <div className="grid gap-4">
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Başlık *</label>
                          <input
                            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                            value={editingItem.baslik}
                            onChange={(e) => setEditingItem((p) => p && { ...p, baslik: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Açıklama</label>
                          <textarea
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                            rows={2}
                            value={editingItem.aciklama ?? ""}
                            onChange={(e) => setEditingItem((p) => p && { ...p, aciklama: e.target.value })}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Süre (dakika)</label>
                            <input
                              type="number"
                              min="0"
                              className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                              value={editingItem.sure_dakika ?? ""}
                              onChange={(e) => setEditingItem((p) => p && { ...p, sure_dakika: e.target.value ? Number(e.target.value) : null })}
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Fiyat (₺)</label>
                            <input
                              type="number"
                              min="0"
                              className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                              value={editingItem.price ?? 0}
                              onChange={(e) => setEditingItem((p) => p && { ...p, price: Number(e.target.value) || 0 })}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Ses Dosyası (ses_url)</label>
                          <input
                            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs text-slate-700 outline-none"
                            value={editingItem.ses_url ?? ""}
                            onChange={(e) => setEditingItem((p) => p && { ...p, ses_url: e.target.value || null })}
                            placeholder="ses dosyası URL'i..."
                          />
                          <div className="mt-2">
                            <SelmaAiVoiceGenerator onGenerated={(url) => setEditingItem((p) => p && { ...p, ses_url: url })} />
                          </div>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Arka Plan Müziği (music_url)</label>
                          <select
                            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                            value={editingItem.music_url ?? ""}
                            onChange={(e) => setEditingItem((p) => p && { ...p, music_url: e.target.value || null })}
                          >
                            <option value="">— Müzik yok —</option>
                            {musicOptions.map((m) => (
                              <option key={m.id} value={m.dosya_url}>{m.isim}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Müzik Ses Seviyesi — {Number(editingItem.music_volume ?? 0.5).toFixed(2)}
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            className="h-10 w-full accent-emerald-600"
                            value={editingItem.music_volume ?? 0.5}
                            onChange={(e) => setEditingItem((p) => p && { ...p, music_volume: Number(e.target.value) })}
                          />
                        </div>
                      </div>
                      {updateMutation.isError && (
                        <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                          {(updateMutation.error as Error).message}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-shrink-0 gap-2 rounded-b-2xl border-t border-slate-200 bg-white p-6 pt-4">
                      <Button type="submit" size="sm" className="rounded-full" disabled={updateMutation.isPending}>
                        {updateMutation.isPending ? "Kaydediliyor…" : "Kaydet"}
                      </Button>
                      <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => setEditingItem(null)}>
                        İptal
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {viewMode === "grid" ? (
                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {visibleItems.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-3 min-w-0">
                          {item.gorsel_url && <img src={item.gorsel_url} alt="" className="h-16 w-16 flex-shrink-0 rounded-xl object-cover" />}
                          <div className="font-bold text-slate-900">{item.baslik}</div>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1">
                          <button type="button" className="rounded-xl p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" onClick={() => setEditingItem(item)}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="rounded-xl p-1.5 text-rose-500 hover:bg-rose-50"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                              if (confirm(`"${item.baslik}" silinsin mi?`)) deleteMutation.mutate(item.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {item.aciklama && <div className="mt-1 line-clamp-2 text-sm text-slate-500">{item.aciklama}</div>}
                      <div className="mt-3 flex flex-wrap gap-1">
                        {(item.kategori_duygu ?? []).map((t, i) => (
                          <span key={`d-${i}`} className="rounded-full bg-emerald-600/10 px-2 py-0.5 text-[11px] font-bold text-emerald-700">{t}</span>
                        ))}
                        {(item.kategori_beden_bolgesi ?? []).map((t, i) => (
                          <span key={`b-${i}`} className="rounded-full bg-amber-600/10 px-2 py-0.5 text-[11px] font-bold text-amber-700">{t}</span>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-slate-400">
                        {item.sure_dakika ? `${item.sure_dakika} dk` : "süre belirtilmemiş"} • {item.price > 0 ? `₺${item.price}` : "Ücretsiz"}
                      </div>
                      {item.ses_url && (
                        <button type="button" onClick={() => handlePlay(item.ses_url!)} className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-600/10 px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-600/20">
                          <Play className="h-3.5 w-3.5" />
                          {playingUrl === item.ses_url ? "Durdur" : "Oynat"}
                        </button>
                      )}
                    </div>
                  ))}
                  {items.length === 0 && !showForm && (
                    <button type="button" onClick={() => setShowForm(true)} className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-white p-8 text-slate-500 hover:border-emerald-400/50 hover:bg-emerald-50/50">
                      <Plus className="mb-2 h-8 w-8" />
                      <div className="font-bold">İlk meditasyonu ekle</div>
                    </button>
                  )}
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-2">
                  {visibleItems.map((item) => (
                    <div key={item.id} className="group flex overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md">
                      <div
                        className="relative w-24 flex-none bg-gradient-to-br from-emerald-600/25 via-amber-600/10 to-slate-950/10 sm:w-36"
                        style={item.gorsel_url ? { backgroundImage: `url(${item.gorsel_url})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
                      />
                      <div className="flex flex-1 items-center gap-3 px-4 py-3">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-bold text-slate-900">{item.baslik}</div>
                          {item.aciklama && <div className="mt-0.5 line-clamp-2 text-xs text-slate-500">{item.aciklama}</div>}
                        </div>
                        <div className="hidden flex-none sm:block">
                          <div className="rounded-xl bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
                            {item.sure_dakika ? `${item.sure_dakika} dk` : "-"}
                          </div>
                        </div>
                        <div className="flex flex-none items-center gap-1">
                          {item.ses_url && (
                            <button type="button" onClick={() => handlePlay(item.ses_url!)} className="grid h-8 w-8 place-items-center rounded-xl bg-emerald-600/10 text-emerald-700 hover:bg-emerald-600/20">
                              <Play className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button type="button" className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200" onClick={() => setEditingItem(item)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-rose-600 hover:bg-rose-50"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                              if (confirm(`"${item.baslik}" silinsin mi?`)) deleteMutation.mutate(item.id);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && !showForm && (
                    <button type="button" onClick={() => setShowForm(true)} className="flex items-center gap-4 rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-4 text-slate-600 transition-colors hover:border-emerald-500/50 hover:bg-emerald-50/50">
                      <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-slate-100 text-slate-900">
                        <span className="text-xl">+</span>
                      </div>
                      <div className="text-sm font-bold">İlk meditasyonu ekle</div>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
